import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Sparkles, Palette, PenTool, Hammer, Layers, GraduationCap } from "lucide-react";
import { publicApi } from "@/lib/api";
import ServiceQuoteDialog from "@/components/ServiceQuoteDialog";
import ShareButton from "@/components/ShareButton";
import ReviewsStrip from "@/components/ReviewsStrip";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/b2owix5e_ChatGPT%20Image%20Jul%202%2C%202026%2C%2003_51_31%20AM.png";

const services = [
  { icon: Palette, title: "Artwork", desc: "Original paintings & commissions" },
  { icon: PenTool, title: "Design", desc: "Brand identity, apparel graphics" },
  { icon: Layers, title: "3D Printing", desc: "Object printing & prototypes" },
  { icon: Hammer, title: "Engraving", desc: "Laser wood & acrylic engraving" },
  { icon: Sparkles, title: "Murals", desc: "Paint your own or let Cam paint it", href: "/services/mural" },
  { icon: GraduationCap, title: "Workshops", desc: "Cultural Arts for schools, parties & groups", href: "/services/workshops" },
  { icon: PenTool, title: "Tattoo Design", desc: "Custom flash & full designs" },
];

const portfolioTiles = [
  { url: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/af81rgkp_print6.png", tall: true },
  { url: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/sbe6lsps_print5.png" },
  { url: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/exd2houl_Copilot_20260625_090035.png" },
  { url: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/h1bes75a_print%201.webp" },
  { url: "https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/6kg5oq7t_print%204.png", tall: true },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [quoteService, setQuoteService] = useState(null);
  const [videos, setVideos] = useState([]);
  useEffect(() => {
    publicApi.listProducts({ featured: true }).then((data) => setFeatured(data)).catch(() => {});
    publicApi.listVideos().then(setVideos).catch(() => {});
  }, []);

  // Split featured products into art (prints/canvas/originals) vs apparel (all wearable categories)
  const APPAREL_CATS = ["apparel", "mens", "womens"];
  const artworks = featured.filter((p) => !APPAREL_CATS.includes(p.category) && p.category !== "diy_kits").slice(0, 4);
  const apparel = featured.filter((p) => APPAREL_CATS.includes(p.category)).slice(0, 4);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Cam's Koori Art hero" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 md:pt-40 pb-16">
          <p className="overline mb-6" data-testid="hero-eyebrow">CamOriginal Apparel · Koori Art · Custom Studio</p>
          <h1 data-testid="hero-title" className="font-display text-6xl sm:text-7xl md:text-[9rem] leading-[0.85] max-w-5xl">
            Story-driven <br />
            <span className="text-[#FF4500]">art</span> in every stitch, <br />
            print & panel.
          </h1>
          <p data-testid="hero-subtitle" className="mt-8 max-w-xl text-white/70 text-lg leading-relaxed">
            Cam's Koori Art blends Indigenous storytelling with street-art energy — apparel, canvas, prints, murals, 3D & engraving, all made to order.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/shop" data-testid="hero-cta-shop" className="btn-primary">Shop the Drop</Link>
            <Link to="/custom" data-testid="hero-cta-custom" className="btn-ghost">Commission a Piece</Link>
            <ShareButton variant="ghost" label="Share" testid="home-share-button" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 border-y border-white/10 bg-[#0A0A0A]/80 backdrop-blur">
          <Marquee gradient={false} speed={40} className="py-4">
            <span className="font-display text-3xl md:text-5xl px-8 text-white/80">APPAREL · </span>
            <span className="font-display text-3xl md:text-5xl px-8 text-[#FF4500]">ARTWORKS · </span>
            <span className="font-display text-3xl md:text-5xl px-8 text-white/80">MURALS · </span>
            <span className="font-display text-3xl md:text-5xl px-8 text-[#FF4500]">TATTOOS · </span>
            <span className="font-display text-3xl md:text-5xl px-8 text-white/80">ENGRAVING · </span>
            <span className="font-display text-3xl md:text-5xl px-8 text-[#FF4500]">3D PRINT · </span>
          </Marquee>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {/* ARTWORKS — canvases, prints, originals */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24" data-testid="section-artworks">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-3">Canvases · Prints · Originals</p>
            <h2 className="font-display text-4xl md:text-6xl">Artworks</h2>
            <p className="text-white/60 mt-3 max-w-lg text-sm">Fine-art prints on premium canvas and paper — signed, numbered, ready to hang.</p>
          </div>
          <Link to="/shop?category=prints" data-testid="artworks-view-all" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-widest hover:text-[#FF4500]">
            All artworks <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.length === 0 ? (
            [...Array(4)].map((_, i) => (
              <div key={`sk-art-${i}`} className="aspect-[3/4] bg-white/5 border border-white/10 animate-pulse" />
            ))
          ) : artworks.length === 0 ? (
            <p className="col-span-full text-white/40 italic text-sm">No featured artworks yet — check the full <Link to="/shop" className="text-[#FF4500] underline">shop</Link>.</p>
          ) : (
            artworks.map((p) => (
              <Link
                key={p.id}
                to={`/shop/${p.id}`}
                data-testid={`featured-product-${p.id}`}
                className="group hover-zoom overflow-hidden"
              >
                <div className="aspect-[3/4] overflow-hidden bg-[#141414]">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="pt-4 flex justify-between items-start">
                  <div>
                    <p className="font-display text-lg tracking-wider">{p.title}</p>
                    <p className="text-xs text-white/50 uppercase tracking-widest">{p.category}</p>
                  </div>
                  <div className="text-right">
                    {p.category === "prints" && <p className="text-[10px] text-white/40 uppercase tracking-widest">From</p>}
                    <p className="text-[#FF4500] font-semibold">${p.price.toFixed(0)}</p>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* APPAREL — hoodies + wearable drops (separate section from artworks) */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24" data-testid="section-apparel">
        <div className="flex items-end justify-between mb-12 border-t border-white/10 pt-16">
          <div>
            <p className="overline mb-3">Wear the story</p>
            <h2 className="font-display text-4xl md:text-6xl">Apparel</h2>
            <p className="text-white/60 mt-3 max-w-lg text-sm">Custom-built hoodies — pick your colourway, pick your logo, drop your name straight onto the fabric.</p>
          </div>
          <Link to="/shop?category=apparel" data-testid="apparel-view-all" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-widest hover:text-[#FF4500]">
            All apparel <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.length === 0 ? (
            [...Array(4)].map((_, i) => (
              <div key={`sk-app-${i}`} className="aspect-[3/4] bg-white/5 border border-white/10 animate-pulse" />
            ))
          ) : apparel.length === 0 ? (
            <p className="col-span-full text-white/40 italic text-sm">No apparel featured yet — see the full <Link to="/shop?category=apparel" className="text-[#FF4500] underline">apparel range</Link>.</p>
          ) : (
            apparel.map((p) => (
              <Link
                key={p.id}
                to={`/shop/${p.id}`}
                data-testid={`featured-product-${p.id}`}
                className="group hover-zoom overflow-hidden"
              >
                <div className="aspect-[3/4] overflow-hidden bg-[#141414]">
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="pt-4 flex justify-between items-start">
                  <div>
                    <p className="font-display text-lg tracking-wider">{p.title}</p>
                    <p className="text-xs text-white/50 uppercase tracking-widest">Apparel</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#FF4500] font-semibold">${p.price.toFixed(0)}</p>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* IN THE MAKING — process videos */}
      {videos.length > 0 && (
        <section data-testid="section-videos" className="border-y border-white/10 bg-[#0A0A0A]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <p className="overline mb-3">In the making</p>
                <h2 className="font-display text-4xl md:text-6xl">Watch the work happen.</h2>
                <p className="text-white/60 max-w-2xl mt-3">Process clips straight from Cam&rsquo;s studio — every brush stroke, layer and finish.</p>
              </div>
              <p className="text-xs uppercase tracking-widest text-white/40">{videos.length} clip{videos.length === 1 ? "" : "s"}</p>
            </div>
            <div className={`grid gap-4 md:gap-6 ${videos.length === 1 ? "grid-cols-1 max-w-3xl" : videos.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
              {videos.map((v) => (
                <figure key={v.id} data-testid={`home-video-${v.id}`} className="border border-white/10 bg-[#141414] overflow-hidden group">
                  <div className="aspect-video bg-black overflow-hidden relative">
                    <video
                      src={v.video_url}
                      controls
                      preload="metadata"
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <figcaption className="p-4 md:p-5">
                    <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold mb-1">Process video</p>
                    <p className="font-display text-lg md:text-xl tracking-wider leading-tight">{v.title}</p>
                    {v.description && <p className="text-xs text-white/60 mt-2 leading-relaxed">{v.description}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ABOUT INTRO */}
      <section className="border-y border-white/10 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 relative aspect-square">
            <img src="https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/pdl5f5xk_meeee.png" alt="Cameron Boyle" className="w-full h-full object-cover object-top" />
            <img
              src="https://customer-assets.emergentagent.com/job_indigenous-merch/artifacts/pftx9pi2_downloadfile-6.png"
              alt="CKA emblem"
              className="absolute -top-6 -left-6 w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-2xl hidden md:block"
              style={{
                WebkitMaskImage:
                  "radial-gradient(circle at center, black 38%, rgba(0,0,0,0.55) 62%, transparent 84%)",
                maskImage:
                  "radial-gradient(circle at center, black 38%, rgba(0,0,0,0.55) 62%, transparent 84%)",
              }}
            />
            <div className="absolute -bottom-4 -right-4 bg-[#FF4500] px-6 py-3 font-display tracking-widest text-lg">EST. YARN</div>
          </div>
          <div className="md:col-span-7">
            <p className="overline mb-4">Meet the Artist</p>
            <h2 className="font-display text-4xl md:text-6xl leading-none mb-8">
              I'm <span className="text-[#FF4500]">Cameron Boyle</span> — <br /> Barkindji & Gunai Kurnai, contemporary Australian artist.
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              My work brings together traditional values with a modern, expressive visual style — bold colours, intricate line work and organic forms inspired by Country, wildlife and story.
            </p>
            <p className="text-white/70 leading-relaxed mb-6">
              Rather than reproducing traditional cultural artworks, I create contemporary pieces that honour connection, resilience and respect — professionally reproduced on premium materials so every collector receives museum-quality prints, canvases and apparel.
            </p>
            <Link to="/about" data-testid="about-cta" className="btn-ghost">Read the full story</Link>
          </div>
        </div>
      </section>

      {/* CUSTOMER REVIEWS STRIP */}
      <ReviewsStrip />

      {/* SERVICES / CUSTOM STUDIO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24" data-testid="section-services">
        <p className="overline mb-3">The Studio</p>
        <h2 className="font-display text-4xl md:text-6xl mb-12">Custom project? Bring it on.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s, i) => (
            s.href ? (
              <Link
                key={s.title}
                to={s.href}
                data-testid={`service-card-${i}`}
                className="group relative bg-[#141414] border border-white/10 p-8 hover:border-[#FF4500] transition-colors text-left block"
              >
                <s.icon className="w-8 h-8 text-[#FF4500] mb-6" />
                <h3 className="font-display text-2xl mb-2 tracking-wider">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">{s.desc}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold">Explore the service →</p>
              </Link>
            ) : (
              <button
                key={s.title}
                data-testid={`service-card-${i}`}
                onClick={() => setQuoteService(s.title)}
                className="group relative bg-[#141414] border border-white/10 p-8 hover:border-[#FF4500] transition-colors text-left"
              >
                <s.icon className="w-8 h-8 text-[#FF4500] mb-6" />
                <h3 className="font-display text-2xl mb-2 tracking-wider">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">{s.desc}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#FF4500] font-semibold">See price guide →</p>
              </button>
            )
          ))}
        </div>
        <ServiceQuoteDialog open={!!quoteService} onOpenChange={(v) => !v && setQuoteService(null)} service={quoteService} />
        <div className="mt-12">
          <Link to="/custom" data-testid="services-cta" className="btn-primary">Start a Custom Request</Link>
        </div>
      </section>

      {/* PORTFOLIO STRIP */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24" data-testid="section-portfolio-strip">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-3">From the vault</p>
            <h2 className="font-display text-4xl md:text-6xl">Recent work</h2>
          </div>
          <Link to="/portfolio" data-testid="portfolio-view-all" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-widest hover:text-[#FF4500]">
            Full portfolio <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 grid-flow-dense gap-4">
          {portfolioTiles.map((t, i) => (
            <div key={i} className={`overflow-hidden bg-[#141414] hover-zoom ${t.tall ? "row-span-2" : ""} ${i === 0 ? "col-span-2" : ""}`}>
              <img src={t.url} alt={`Portfolio ${i}`} className={`w-full ${t.tall ? "h-full" : "h-64"} object-cover`} />
            </div>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="border-y border-white/10 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "150+", l: "Custom commissions" },
            { n: "12", l: "Murals painted" },
            { n: "8k", l: "Community reach" },
            { n: "100%", l: "Story-driven" },
          ].map((s, i) => (
            <div key={i} data-testid={`trust-stat-${i}`}>
              <p className="font-display text-5xl md:text-6xl text-[#FF4500]">{s.n}</p>
              <p className="text-white/60 text-xs uppercase tracking-widest mt-2">{s.l}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
